import React, { useState, useEffect, useRef } from 'react';
import { Terminal } from 'lucide-react';
import { useAchievements } from './AchievementSystem';

const InteractiveTerminal = () => {
  const [input, setInput] = useState('');
  const [history, setHistory] = useState([
    { type: 'system', text: '🛡️ Welcome to Captain DevSecOps Terminal v2.0' },
    { type: 'system', text: 'Type "help" for available commands' },
  ]);
  const inputRef = useRef(null);
  const terminalRef = useRef(null);
  
  // Try to get achievements context (may be null if not wrapped)
  let unlockAchievement = () => {};
  try {
    const achievements = useAchievements();
    if (achievements) {
      unlockAchievement = achievements.unlockAchievement;
    }
  } catch (e) {}

  const handleTerminalClick = () => {
    inputRef.current?.focus();
    unlockAchievement('terminal_hacker');
  };

  const commands = {
    help: () => [
      '📋 Available commands:',
      '  about      - Learn about Rohit Majithia',
      '  skills     - View technical superpowers',
      '  experience - View career history',
      '  certs      - View certifications',
      '  deploy     - Deploy to production (safely!)',
      '  hack       - Try to hack this site',
      '  status     - Check cluster status',
      '  coffee     - Essential DevOps fuel',
      '  clear      - Clear terminal',
      '  contact    - How to reach me',
      '  whoami     - Who is this DevSecOps hero?',
    ],
    about: () => [
      '🦸 About Rohit Majithia',
      '━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━',
      '',
      '   DevSecOps Engineer & AWS Solutions Architect',
      '   with 6+ years of experience in cloud automation,',
      '   Kubernetes orchestration, and security-first infrastructure.',
      '',
      '   🎯 Mission: Securing Kubernetes at scale',
      '   📍 Location: Ahmedabad, India',
      '   💼 Current: DevOps Engineer @ Confidential Organization',
      '',
      '   "Engineering zero-trust cloud systems that scale."',
    ],
    skills: () => [
      '⚡ Technical Superpowers:',
      '━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━',
      '',
      '   ☁️  Cloud Platforms',
      '       └── AWS (EC2, EKS, Lambda, S3, RDS)',
      '       └── Azure (AKS, Functions, DevOps)',
      '       └── GCP (GKE, Cloud Run)',
      '',
      '   🐳 Containers & Orchestration',
      '       └── Kubernetes ████████████ 95%',
      '       └── Docker, Helm, ArgoCD, Istio',
      '',
      '   🏗️  Infrastructure as Code',
      '       └── Terraform  ███████████░ 92%',
      '       └── CloudFormation, Ansible, Pulumi',
      '',
      '   🔒 Security & Compliance',
      '       └── Vault, OPA/Gatekeeper, Trivy',
      '       └── Zero-trust architecture',
    ],
    experience: () => [
      '💼 Career Journey:',
      '━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━',
      '',
      '   🟢 DevOps Engineer @ Confidential Org (2025-Present)',
      '      └── Leading cloud automation & K8s orchestration',
      '      └── Security-first DevSecOps practices',
      '',
      '   🔵 DevOps Engineer @ Anblicks (2020-2025)',
      '      └── 99.99% uptime on production clusters',
      '      └── Reduced deployment time by 70%',
      '      └── AWS-to-Azure migration, zero downtime',
      '',
      '   🟣 AWS Associate @ Discus Business (2019-2020)',
      '      └── AWS infrastructure management',
      '      └── Cloud migrations & automation',
    ],
    certs: () => [
      '🏆 Certifications:',
      '━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━',
      '',
      '   ⭐ AWS Solutions Architect - Professional (2024)',
      '   ⭐ Certified Kubernetes Administrator - CKA (2024)',
      '   ⭐ HashiCorp Terraform Associate (2023)',
      '   ⭐ Azure Administrator AZ-104 (2024)',
      '   ⭐ AWS Solutions Architect - Associate (2023)',
    ],
    whoami: () => [
      '🦸 Rohit Majithia',
      '   Role: DevSecOps Engineer & AWS Solutions Architect',
      '   Mission: Securing Kubernetes at scale',
      '   Status: Currently saving clusters... 🚀',
    ],
    deploy: () => {
      setTimeout(() => {
        setHistory(prev => [...prev, 
          { type: 'success', text: '✅ Deployment successful! Zero downtime achieved.' },
          { type: 'info', text: '📊 Metrics: 0 errors, 100% health checks passed' },
        ]);
      }, 1500);
      return [
        '🚀 Initiating deployment pipeline...',
        '   ├── Running security scans... ✓',
        '   ├── Building containers... ✓',
        '   ├── Pushing to registry... ✓',
        '   └── Rolling update in progress...',
      ];
    },
    hack: () => {
      unlockAchievement('tried_to_hack');
      return [
        '🛡️ Nice try! But this cluster is protected by:',
        '   ├── Zero-trust architecture',
        '   ├── Network policies',
        '   ├── Pod security standards',
        '   ├── OPA Gatekeeper',
        '   └── ...and Captain DevSecOps! 💪',
        '',
        '🔒 Access Denied. Security score: 100/100',
      ];
    },
    status: () => [
      '📊 Cluster Status Dashboard:',
      '   ├── prod-cluster    ● HARDENED',
      '   ├── staging-cluster ● POLICY ENFORCED',
      '   └── secure-eks      ● ZERO-TRUST',
      '',
      '   Uptime: 99.99% | Pods: 247 | Nodes: 12',
    ],
    coffee: () => {
      unlockAchievement('coffee_lover');
      return [
        '☕ Brewing DevOps fuel...',
        '',
        '   ██████████████ 100%',
        '',
        '   Coffee status: READY',
        '   Side effects: +50% productivity',
        '   Warning: May cause excessive deployments',
      ];
    },
    contact: () => [
      '📬 Let\'s connect!',
      '   ├── LinkedIn: /in/rohit-majithia',
      '   ├── GitHub: @rohitsmajithia',
      '   ├── Instagram: @devsecopswithrohit',
      '   └── Scroll down to the contact form! 👇',
    ],
    clear: () => {
      setHistory([
        { type: 'system', text: '🛡️ Terminal cleared. Type "help" for commands.' },
      ]);
      return [];
    },
  };

  const handleCommand = (cmd) => {
    const trimmed = cmd.trim().toLowerCase();
    setHistory(prev => [...prev, { type: 'input', text: `$ ${cmd}` }]);
    
    if (trimmed === 'clear') {
      commands.clear();
      return;
    }
    
    if (commands[trimmed]) {
      const output = commands[trimmed]();
      if (output.length > 0) {
        setHistory(prev => [...prev, ...output.map(text => ({ type: 'output', text }))]);
      }
    } else if (trimmed) {
      setHistory(prev => [...prev, 
        { type: 'error', text: `Command not found: ${trimmed}` },
        { type: 'info', text: 'Type "help" to see available commands' },
      ]);
    }
    setInput('');
  };

  useEffect(() => {
    if (terminalRef.current) {
      terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
    }
  }, [history]);

  return (
    <div 
      className="bg-gray-900 rounded-2xl overflow-hidden shadow-2xl border border-gray-700 cursor-text"
      onClick={handleTerminalClick}
      data-testid="interactive-terminal"
    >
      {/* Terminal Header */}
      <div className="bg-gray-800 px-4 py-2 flex items-center gap-2 border-b border-gray-700">
        <div className="flex gap-1.5">
          <div className="w-3 h-3 rounded-full bg-red-500" />
          <div className="w-3 h-3 rounded-full bg-yellow-500" />
          <div className="w-3 h-3 rounded-full bg-green-500" />
        </div>
        <div className="flex-1 text-center">
          <span className="text-gray-400 text-xs font-mono flex items-center justify-center gap-2">
            <Terminal className="w-3 h-3" />
            captain-devsecops@secure-cluster
          </span>
        </div>
      </div>
      
      {/* Terminal Body */}
      <div 
        ref={terminalRef}
        className="p-4 h-72 overflow-y-auto font-mono text-sm"
      >
        {history.map((item, i) => (
          <div 
            key={i} 
            className={`
              ${item.type === 'input' ? 'text-green-400' : ''}
              ${item.type === 'output' ? 'text-gray-300' : ''}
              ${item.type === 'system' ? 'text-purple-400' : ''}
              ${item.type === 'error' ? 'text-red-400' : ''}
              ${item.type === 'success' ? 'text-green-400' : ''}
              ${item.type === 'info' ? 'text-blue-400' : ''}
              whitespace-pre-wrap leading-relaxed
            `}
          >
            {item.text}
          </div>
        ))}
        
        {/* Input Line */}
        <div className="flex items-center text-green-400 mt-1">
          <span>$ </span>
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleCommand(input)}
            className="flex-1 bg-transparent outline-none text-green-400 caret-green-400"
            placeholder="type a command..."
            autoComplete="off"
            data-testid="terminal-input"
          />
          <span className="animate-pulse">▊</span>
        </div>
      </div>
    </div>
  );
};

export default InteractiveTerminal;
